/**
 * Birdify product facts shown on /apps and /apps/birdify.
 *
 * Feature statements are taken from Birdify's public-page drafts
 * (docs/web/index.html, docs/web/support.html) and privacy policy
 * (docs/privacy-policy.html) in https://github.com/theKeval/Birdify. Do not add
 * claims (user counts, ratings, download numbers) that those sources do not
 * establish. Birdify is published on Google Play; the store listing is the
 * source of truth for availability.
 */
import { APPS_ROUTES } from '../../screens/apps/routes';
import iconImage from '../../assets/images/birdify/app-icon.png';

const NO_ADS = { label: 'No ads or purchases', variant: 'outline' };

export const birdify = {
  id: 'birdify',
  name: 'Birdify',
  iconImage,
  tagline: 'Listen. Identify. Learn.',
  summary: 'A free educational Android app for exploring the birds around you. Bird identification runs on your device.',
  platform: 'Android',
  packageName: 'io.keval.apps.birdify',
  operator: 'Keval Langalia',
  contactEmail: 'hello@keval.io',
  route: APPS_ROUTES.birdify,
  privacyRoute: APPS_ROUTES.birdifyPrivacy,
  termsRoute: APPS_ROUTES.birdifyTerms,
  // The /apps card shows no chips; the product page header shows only `heroBadges`.
  badges: [],
  heroBadges: [NO_ADS],
  // The privacy policy is reachable from the end of the product page, not from the card.
  cardPrivacyLink: false,
  release: {
    playStoreUrl: 'https://play.google.com/store/apps/details?id=io.keval.apps.birdify',
  },
  features: [
    {
      id: 'local-identification',
      title: 'Bird identification on your device',
      body: 'Record a bird sound and Birdify analyzes the audio locally with BirdNET. Your recording is not uploaded for identification, and location is not an input to the model. Once the model and labels are installed you can identify birds offline. Results are estimates to help you explore and learn.',
    },
    {
      id: 'import',
      title: 'Import audio or video',
      body: 'Select an audio or video file through Android\u2019s document picker. Birdify decodes up to the first three minutes locally and analyzes that audio. Importing does not need microphone permission and leaves the original file untouched.',
    },
    {
      id: 'history',
      title: 'Keep your discoveries',
      body: 'Choose Save or Discard after each recording or import. Saved sessions keep their audio and results in private app storage, and history holds the latest 20 sessions so you can replay what you heard. In Detection History, you can remove one saved session from its session menu or remove all currently saved sessions through History actions with confirmation.',
    },
    {
      id: 'enrichment',
      title: 'Explore further when online',
      optional: true,
      body: 'Online features can add bird information and images from Wikipedia, Wikimedia Commons and the Encyclopedia of Life, licensed reference recordings from Xeno-canto, and optional nearby sightings from eBird. These requests involve external providers and network information; the privacy policy explains each one.',
    },
  ],
  faq: [
    {
      question: 'Can I identify birds without internet?',
      answer: 'Yes, once the BirdNET model and labels are installed. Online information, images and reference recordings need network access.',
    },
    {
      question: 'Do I have to share my location?',
      answer: 'No. You can decline the optional nearby-sightings feature and still identify birds locally. You can manage location and microphone permissions in Android settings.',
    },
    {
      question: 'How do I remove saved sessions?',
      answer: 'In Detection History, open a saved session menu and choose Delete session, or open History actions and choose Delete all sessions to remove everything currently saved when you confirm. You can also delete the open session from the session detail menu. Deletion removes the selected local history records, saved coordinates and Birdify-managed saved audio that no remaining session references. If managed-audio cleanup cannot finish, Birdify reports it and retries cleanup at app startup. Android Clear storage or uninstall are broader alternatives that also remove app settings. Clear cache alone does not remove saved history, and none of these actions remove an imported original file, earlier backups or information already processed by external services.',
    },
    {
      question: 'Are identification results certain?',
      answer: 'No. Background noise, recording quality and similar bird sounds can affect results. Treat detections and confidence estimates as learning aids.',
    },
  ],
};
